'use client';
import { useState, useEffect } from "react";
import { trpc } from "../utils/trpc";
import "../styles/CreateTagModal.css";

interface Category {
  id: number;
  name: string;
}

interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

interface CreateTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTagCreated: (tag: Tag) => void; // callback receives the real tag
}

function CreateTagModal({ isOpen, onClose, onTagCreated }: CreateTagModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [tagName, setTagName] = useState("");
  const [error, setError] = useState("");

  const utils = trpc.useUtils();
  const createTag = trpc.tags.createTag.useMutation();

  // Fetch categories when modal opens
  const { data: fetchedCategories } = trpc.tags.getAllCategoriesWithTags.useQuery(undefined, {
    enabled: isOpen,
  });

  useEffect(() => {
    if (fetchedCategories) {
      const categories = fetchedCategories.map((category) => ({ id: category.id, name: category.name }));
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCategories(categories);
      setSelectedCategory(undefined);
    }
  }, [fetchedCategories]);

  const handleCreate = async () => {
    const normalizedName = tagName.trim().toLowerCase();
    if (!normalizedName) {
      setError("Interest name cannot be empty.");
      return;
    }
    if (!selectedCategory) {
      setError("Please select a category.");
      return;
    }

    try {
      // Call backend to create tag
      const newTag = await createTag.mutateAsync({
        name: normalizedName,
        categoryId: selectedCategory,
      });
      console.log("Interest created successfully:", newTag);

      // Pass the real tag back to TagManagerModal
      onTagCreated(newTag);

      // Refresh categories in case needed
      await utils.tags.getAllCategoriesWithTags.invalidate();

      // Reset local state
      setTagName("");
      setSelectedCategory(undefined);
      setError("");
      onClose();
    } catch (err: unknown) {
      console.error("Error creating interest:", err);
      setError("Failed to create interest. Try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-tag-modal-overlay">
      <div className="create-tag-modal-content">
        <div className="create-tag-modal-header">
          <h2>Create New Tag</h2>
          <button className="create-tag-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="create-tag-modal-body">
          <label>
            Select Category
            <select
                value={selectedCategory ?? ""}
                onChange={(e) =>
                    setSelectedCategory(
                    e.target.value ? Number(e.target.value) : undefined
                    )
                }
                >
                <option value="" disabled>Select a category</option>

                {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                    {category.name}
                    </option>
                ))}
                </select>

          </label>

          <label>
            Interest Name
            <input
              type="text"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Enter new interest name"
            />
          </label>

          {error && <p className="create-tag-error-text">{error}</p>}
        </div>

        <div className="create-tag-modal-actions">
          <button className="create-tag-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="create-tag-save-btn" onClick={handleCreate}>Create Interest</button>
        </div>
      </div>
    </div>
  );
}

export default CreateTagModal;




